import React, { useEffect, useState, useRef } from "react";
import { useToggle } from "react-use";

import CloseIcon from "public/icons/close.svg";

import { Theme, useMediaQuery, styled, css } from "@mui/material";

import { Button } from "@/components/form/Button/Button";
import { SvgIcon } from "@/components/ui/svg-icon/SvgIcon";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import { LadyDtoToggleMediaFavorite } from "@/features/lady-profile/hooks";
import { skipForwardProp } from "@/utils/skipForwardProp";
import { NumberWithPostfix, stringMath } from "@/utils/string.utils";
import { clsxm } from "@/utils/tailwindMerge";

import { CommonModalProps, LadySliderModalMedia } from "../../models";
import { PlayCircleIcon, PrivatePhotoAttachment } from "./components";
import {
  LadySliderModalBottomButtons,
  LadySliderModalBottomButtonsPropTypes,
} from "./components/bottom-buttons";
import { FavoriteButtonController } from "./components/favorite-button";
import { SliderPhoto } from "./components/slider-photo";
import { useSliderContext } from "./context/Slider.context";
import { helper } from "./helpers";
import { useSliderNavigation } from "./hook";

const { isLadyPhoto, isAnyVideo } = helper;
const MARGIN_BETWEEN_PHOTOS = 15;
const THUMBNAIL_PHOTO_WIDTH = "115px";

type CommonPropTypes = CommonModalProps & {
  media?: LadySliderModalMedia;
  isOnline?: boolean;
  isCamAvailable?: boolean;
  ladyId?: number;
  alt: string;
  initialIndex: number;
  isFavoriteIconDisabled?: boolean;
  toggleMediaFavorite?: LadyDtoToggleMediaFavorite;
};

type PropTypesWithBottomButtons = CommonPropTypes & {
  isBottomButtons: true;
  bottomButtonsProps: LadySliderModalBottomButtonsPropTypes;
};

type PropTypesWithoutBottomButtons = CommonPropTypes & {
  isBottomButtons?: false;
};

type PropTypes = PropTypesWithBottomButtons | PropTypesWithoutBottomButtons;

export const LadySliderModal: React.FC<PropTypes> = (props: PropTypes) => {
  const {
    onClose,
    media,
    alt,
    initialIndex = 0,
    isOnline,
    ladyId,
    isFavoriteIconDisabled,
    toggleMediaFavorite,
    isBottomButtons,
  } = props;

  const { isZoomed, toggleIsZoomed } = useSliderContext();

  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(initialIndex);

  useEffect(() => {
    toggleIsZoomed(false);
  }, [currentPhotoIndex, toggleIsZoomed]);

  const { navigation } = useSliderNavigation({
    mediaLength: media?.length ?? 0,
    setCurrentPhotoIndex,
  });

  const upMd = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const upXl = useMediaQuery((theme: Theme) => theme.breakpoints.up("xl"));
  const selectedPhotoWidth = upXl ? "900px" : upMd ? "490px" : "80vw";

  const selectedPhotoHalfWidth = divideByTwo(selectedPhotoWidth);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoPlaying, toggleVideoIsPlaying] = useToggle(false);

  useEffect(() => {
    const isInitialClickOnVideo =
      videoRef?.current?.dataset.ind === initialIndex.toString();

    if (isInitialClickOnVideo) {
      videoRef.current.play();
    }
  }, [initialIndex]);

  useEffect(() => {
    const isCurrentVideoIndex =
      videoRef?.current?.dataset.ind === currentPhotoIndex.toString();

    if (!isCurrentVideoIndex) {
      videoRef.current?.pause();
    }
  }, [currentPhotoIndex, initialIndex]);

  useEffect(() => {
    const videoNode = videoRef.current;

    const playVideo = () => {
      toggleVideoIsPlaying(true);
    };
    const pauseVideo = () => {
      toggleVideoIsPlaying(false);
    };

    videoNode?.addEventListener("play", playVideo);
    videoNode?.addEventListener("pause", pauseVideo);

    return () => {
      videoNode?.removeEventListener("play", playVideo);
      videoNode?.removeEventListener("pause", pauseVideo);
    };
  }, [toggleVideoIsPlaying]);

  const onVideoToggle = () => {
    if (isVideoPlaying) {
      videoRef.current?.pause();
    } else {
      videoRef.current?.play();
    }

    toggleVideoIsPlaying();
  };

  const onPlaceholderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const { isAuthenticated } = useAuthContext();

  if (!media) return null;

  return (
    <div className="overflow-hidden h-screen">
      <PlaceholderWrapper
        className={clsxm(
          "md:py-6 py-3 select-none w-screen",
          "flex flex-col gap-2",
        )}
        onClick={onPlaceholderClick}
      >
        <div
          className={clsxm(
            "absolute top-4 right-4 z-[1]",
            "md:top-6 md:right-6",
            "cursor-pointer",
          )}
          onClick={onClose}
        >
          <SvgIcon color="white" width={32} height={32}>
            <CloseIcon />
          </SvgIcon>
        </div>
        <MediaWrapper
          currentPhotoIndex={currentPhotoIndex}
          thumbnailPhotoWidth={THUMBNAIL_PHOTO_WIDTH}
          selectedPhotoHalfWidth={selectedPhotoHalfWidth ?? 0}
          isBottomButtons={isBottomButtons}
          isOnline={isOnline}
        >
          {media.map((obj, i) => {
            const isSelected = currentPhotoIndex === i;
            const currentWidth = isSelected
              ? selectedPhotoWidth
              : THUMBNAIL_PHOTO_WIDTH;

            return (
              <PresentationWrapper
                currentWidth={currentWidth}
                key={i}
                onClick={(e) => {
                  if (isSelected) return;
                  setCurrentPhotoIndex(() => i);
                  onPlaceholderClick(e);
                }}
              >
                <div className="flex flex-col h-full justify-center items-center">
                  <ImageWrapper>
                    {isLadyPhoto(obj) && (
                      <SliderPhoto
                        src={obj.photoSrcs.large}
                        alt={alt}
                        isSelected={isSelected}
                        id={`profile-photo-${i}`}
                      />
                    )}

                    {!isZoomed &&
                    !isFavoriteIconDisabled &&
                    isSelected &&
                    isAuthenticated &&
                    ((obj.type === "photo" &&
                      obj.photoType !== "PRIVATE_PHOTO") ||
                      obj.type === "intro_video") ? (
                      <FavoriteButtonController
                        favoriteType={obj.type === "photo" ? "PHOTO" : "VIDEO"}
                        isFavorite={obj.isFavorite}
                        objectId={obj.type === "photo" ? obj.photoId : obj.id}
                        toggleMediaFavorite={toggleMediaFavorite}
                      />
                    ) : null}

                    {isSelected &&
                      obj.type === "photo" &&
                      obj.photoType === "PRIVATE_PHOTO" && (
                        <PrivatePhotoAttachment
                          ladyId={ladyId}
                          isOnline={isOnline}
                        />
                      )}
                    {isAnyVideo(obj) && (
                      <>
                        <video
                          controls={isSelected}
                          data-ind={i}
                          controlsList="nodownload"
                          ref={videoRef}
                          className="rounded-lg max-h-[90vh] cursor-pointer"
                        >
                          <source
                            // adding #t=0.001 to the end of the source to fix the issue with the first frame not showing on iOS
                            type="video/mp4"
                            src={(obj.source || "") + "#t=0.001"}
                          />
                        </video>
                        {!isVideoPlaying && (
                          <PlayCircleIcon
                            ignoreNotDisplayingOnMobile={!isSelected}
                            className={clsxm(
                              isSelected && "top-1/4 bottom-1/4",
                            )}
                            onClick={isSelected ? onVideoToggle : undefined}
                          />
                        )}
                      </>
                    )}
                    {!!isSelected && !isZoomed && (
                      <div
                        className={clsxm(
                          "absolute top-1/2 -translate-y-1/2 left-2 right-2",
                          "flex justify-between pointer-events-none min-w-min z-[1]",
                        )}
                      >
                        <StyledButton
                          variant="text"
                          onClick={navigation("prev")}
                          className={clsxm(
                            "pointer-events-auto",
                            currentPhotoIndex < 1 && "invisible",
                          )}
                        />
                        <StyledButton
                          variant="text"
                          onClick={navigation("next")}
                          className={clsxm(
                            "pointer-events-auto",
                            currentPhotoIndex > media.length - 2 && "invisible",
                          )}
                          nextArrow
                        />
                      </div>
                    )}
                  </ImageWrapper>
                </div>
              </PresentationWrapper>
            );
          })}
        </MediaWrapper>
        {isBottomButtons && (
          <LadySliderModalBottomButtons {...props.bottomButtonsProps} />
        )}
      </PlaceholderWrapper>
    </div>
  );
};

const divideByTwo = (str: NumberWithPostfix) => {
  return stringMath(str, (num) => num / 2);
};

const ImageWrapper = styled("div")(() => ({
  position: "relative",
  overflow: "clip",
  borderRadius: "15px",
  maxHeight: "100%",
  maxWidth: "max-content",
}));

type StyledImgProps = {
  currentWidth: string;
};

const PresentationWrapper = styled("div")(
  ({ currentWidth }: StyledImgProps) => ({
    transition: "all 0.5s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    width: currentWidth,
    paddingInline: `${MARGIN_BETWEEN_PHOTOS}px}`,
  }),
);

type MediaWrapperProps = {
  thumbnailPhotoWidth: string;
  currentPhotoIndex: number;
  selectedPhotoHalfWidth: string;
  isBottomButtons?: boolean;
  isOnline?: boolean;
};

const MediaWrapper = styled("div")(
  ({
    thumbnailPhotoWidth,
    currentPhotoIndex,
    selectedPhotoHalfWidth,
    isBottomButtons,
  }: MediaWrapperProps) => {
    const leftPosition = `calc(50vw - ${
      parseFloat(thumbnailPhotoWidth) * currentPhotoIndex
    }px - ${selectedPhotoHalfWidth})`;

    return {
      display: "flex",
      position: "relative",
      width: "max-content",

      height: !!isBottomButtons ? "calc(100% - 60px)" : "100%",
      overflowX: "clip",
      transition: "all 0.5s ease",
      flexShrink: 0,
      left: leftPosition,
    };
  },
);

const PlaceholderWrapper = styled("div")(
  // using 100% when browser is not supporting dvh units
  // else 100% will be overwritten by 100dvh
  () => css`
    height: 100%;
    height: 100dvh;
  `,
);

type StyledButtonProps = {
  nextArrow?: boolean;
};

const StyledButton = styled(Button, {
  shouldForwardProp: skipForwardProp(["nextArrow"]),
})<StyledButtonProps>(({ theme, nextArrow }) => {
  return {
    backgroundColor: theme.palette.grey?.[900] + " !important",
    opacity: 0.5,
    width: "40px !important",
    height: "40px !important",
    borderRadius: "50% !important",
    "&:after": {
      width: "13px",
      height: "13px",
      borderRight: "2px solid #fff",
      borderBottom: "2px solid #fff",
      content: "''",
      top: "50%",
      left: `${!!nextArrow ? "45%" : "55%"}`,
      margin: "-0.3rem 0 0 -0.3rem",
      transform: `rotate(${!!nextArrow ? -45 : 135}deg)`,
      position: "absolute",
    },
  };
});
